import React, { useState, useEffect } from 'react';
import { 
  User, 
  Calendar, 
  Mail, 
  Phone, 
  Globe, 
  Package, 
  ChevronRight,
  AlertCircle,
  Database,
  Shield,
  TrendingUp,
  DollarSign,
  Key,
  MapPin,
  Link,
  Activity,
  CheckCircle,
  XCircle
} from 'lucide-react';

// Import from your Firebase config
import { database, ref, onValue } from '../../firebaseConfig';

const LatestRegistrations = () => {
  const [latestMembers, setLatestMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMember, setExpandedMember] = useState(null);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalInvestment: 0,
    todayRegistrations: 0
  });

  // Fetch all users from Firebase
  useEffect(() => {
    const usersRef = ref(database, 'users');
    
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      
      if (data) {
        let userList = [];
        let totalInvestment = 0;
        let activeUsersCount = 0;
        let todayRegistrations = 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // Loop through all date folders in users
        for (const [dateKey, dateData] of Object.entries(data)) {
          // Check if dateData is an object (should be a date folder like "2026-02-06")
          if (dateData && typeof dateData === 'object') {
            // Loop through all users in this date folder
            for (const [userId, userData] of Object.entries(dateData)) {
              // Check if userData exists
              if (userData && typeof userData === 'object') {
                
                // Skip if not a user object (has email, name, etc.)
                if (!userData.email && !userData.name) {
                  continue;
                }
                
                // Get registration date
                const regDate = userData.registrationDate || dateKey;
                
                // Count today's registrations
                if (regDate) {
                  const regDateObj = new Date(regDate);
                  if (regDateObj >= today) {
                    todayRegistrations++;
                  }
                }
                
                // Count active users
                if (userData.status === 'active') {
                  activeUsersCount++;
                }
                
                // Get package info
                let packageName = 'No Package';
                let packageAmount = 0;
                
                if (userData.packages && Array.isArray(userData.packages)) {
                  const activePackage = userData.packages.find(pkg => pkg.status === 'active');
                  if (activePackage) {
                    packageName = activePackage.packageName || activePackage.packageId || 'Active Package';
                    packageAmount = parseFloat(activePackage.baseAmount) || 0;
                    totalInvestment += packageAmount;
                  }
                }
                
                // Check for direct investment field
                if (userData.investmentAmount) {
                  const investment = parseFloat(userData.investmentAmount) || 0;
                  totalInvestment += investment;
                }
                
                // Get location info
                const registrationLocation = userData.registrationLocation || {};
                const lastLoginLocation = userData.lastLoginLocation || {};
                
                userList.push({
                  id: userId,
                  dateFolder: dateKey,
                  memberId: userData.memberId || userData.userId || userId,
                  fullName: userData.name || userData.fullName || 'N/A',
                  email: userData.email || '',
                  mobile: userData.mobile || userData.phone || 'N/A',
                  country: userData.country || registrationLocation.country || 'N/A',
                  registrationDate: regDate,
                  registrationLocation: registrationLocation,
                  lastLogin: userData.lastLogin || '',
                  lastLoginLocation: lastLoginLocation,
                  packageName: packageName,
                  packageAmount: packageAmount,
                  sponsorId: userData.sponsorId || userData.referrerId || '',
                  referralLink: userData.referralLink || '',
                  status: userData.status || 'inactive',
                  role: userData.role || 'user',
                  investment: userData.investmentAmount || 0,
                  walletBalance: userData.walletBalance || 0,
                  fund: userData.fund || 0,
                  referralCount: userData.referralCount || 0,
                  dbKey: userData.dbKey || userId,
                  password: userData.password || ''
                });
              }
            }
          }
        }
        
        // If no users found in users node, check root level as fallback
        if (userList.length === 0) {
          const rootRef = ref(database);
          onValue(rootRef, (rootSnapshot) => {
            const rootData = rootSnapshot.val();
            if (rootData) {
              userList = processRootLevelUsers(rootData);
              setStats({
                totalUsers: userList.length,
                activeUsers: activeUsersCount,
                totalInvestment: totalInvestment,
                todayRegistrations: todayRegistrations
              });
            }
          });
        } else {
          // Sort by registration date (newest first)
          userList.sort((a, b) => {
            const dateA = a.registrationDate ? new Date(a.registrationDate) : new Date(0);
            const dateB = b.registrationDate ? new Date(b.registrationDate) : new Date(0);
            return dateB - dateA;
          });
          
          // Get only the latest 20 registrations
          const latestUsers = userList.slice(0, 20);
          
          setLatestMembers(latestUsers);
          setStats({
            totalUsers: userList.length,
            activeUsers: activeUsersCount,
            totalInvestment: totalInvestment,
            todayRegistrations: todayRegistrations
          });
        }
      } else {
        setLatestMembers([]);
      }
      setLoading(false);
      setError(null);
    }, (error) => {
      console.error("Error fetching latest registrations:", error);
      setError("Failed to load latest registrations");
      setLoading(false);
    });
    
    // Cleanup
    return () => {
      // Cleanup if needed
    };
  }, []);

  // Fallback function to process root level users
  const processRootLevelUsers = (data) => {
    let userList = [];
    
    for (const [key, value] of Object.entries(data)) {
      // Skip non-user nodes
      if (['Packages', 'ROISchedule', 'UserFundTransactions', 'admin', 'rewardSettings', 'message'].includes(key)) {
        continue;
      }
      
      if (value && typeof value === 'object') {
        // Check if it has user-like properties
        if (value.email || value.name || value.mobile) {
          
          // Skip admin user
          if (value.userId === 'admin' || value.role === 'admin') {
            continue;
          }
          
          userList.push({
            id: key,
            dateFolder: 'root',
            memberId: value.memberId || value.userId || key,
            fullName: value.name || value.fullName || 'N/A',
            email: value.email || '',
            mobile: value.mobile || value.phone || 'N/A',
            country: value.country || value.location || 'N/A',
            registrationDate: value.registrationDate || value.createdAt || '',
            registrationLocation: value.registrationLocation || {},
            lastLogin: value.lastLogin || '',
            lastLoginLocation: value.lastLoginLocation || {},
            packageName: 'No Package',
            packageAmount: 0,
            sponsorId: value.sponsorId || value.referrerId || '',
            referralLink: value.referralLink || '',
            status: value.status || 'inactive',
            role: value.role || 'user',
            investment: value.investmentAmount || 0,
            walletBalance: value.walletBalance || 0,
            fund: value.fund || 0,
            referralCount: value.referralCount || 0,
            dbKey: value.dbKey || key,
            password: value.password || ''
          });
        }
      }
    }
    
    return userList;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return 'Invalid Date';
    }
  };

  // Format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      return `${formattedHours}:${minutes} ${ampm}`;
    } catch {
      return '';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Get location string from location object
  const getLocationString = (location) => {
    if (!location || typeof location !== 'object') return 'Unknown';
    
    const { city, region, country, ipAddress } = location;
    let locationStr = '';
    
    if (city && city !== 'Unknown') locationStr += city;
    if (region && region !== 'Unknown') {
      if (locationStr) locationStr += ', ';
      locationStr += region;
    }
    if (country && country !== 'Unknown') {
      if (locationStr) locationStr += ', ';
      locationStr += country;
    }
    if (ipAddress && ipAddress !== 'Unknown') {
      if (locationStr) locationStr += ` (${ipAddress})`;
    }
    
    return locationStr || 'Unknown Location';
  };

  // Toggle member details
  const toggleMemberDetails = (id) => {
    setExpandedMember(expandedMember === id ? null : id);
  };

  // Mobile Card Component
  const MobileMemberCard = ({ member, index }) => (
    <div key={member.id} className="bg-secondary/50 border border-border rounded-xl p-4 mb-3">
      {/* Top Row - Sponsor ID First */}
      <div className="mb-3 p-2 bg-secondary rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-purple-400" />
            <div>
              <div className="text-xs text-silver">Sponsor ID</div>
              <div className="text-sm font-bold text-foreground">{member.sponsorId || 'N/A'}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs text-silver">#{index + 1}</div>
            <div className={`px-2 py-0.5 rounded-full text-xs mt-1 ${
              member.status === 'active' 
                ? 'bg-green-500/20 text-green-400' 
                : 'bg-red-500/20 text-red-400'
            }`}>
              {member.status}
            </div>
          </div>
        </div>
      </div>

      {/* Member Info Row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full border flex items-center justify-center ${
            member.status === 'active' 
              ? 'bg-green-500/20 border-green-500/30' 
              : 'bg-red-500/20 border-red-500/30'
          }`}>
            <User className={`h-5 w-5 ${
              member.status === 'active' ? 'text-green-400' : 'text-red-400'
            }`} />
          </div>
          <div>
            <div className="font-bold text-foreground text-sm">{member.fullName}</div>
            <div className="text-xs text-silver">ID: {member.memberId}</div>
          </div>
        </div>
      </div>

      {/* Basic Info Row */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="bg-secondary p-2 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Mail className="h-3 w-3 text-primary" />
            <div className="text-xs text-silver">Email</div>
          </div>
          <div className="text-xs text-foreground truncate">{member.email}</div>
        </div>
        
        <div className="bg-secondary p-2 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <Phone className="h-3 w-3 text-primary" />
            <div className="text-xs text-silver">Mobile</div>
          </div>
          <div className="text-xs text-foreground">{member.mobile}</div>
        </div>
      </div>

      {/* Country Info */}
      <div className="mb-3 bg-secondary p-2 rounded-lg">
        <div className="flex items-center gap-2 mb-1">
          <Globe className="h-3 w-3 text-blue-400" />
          <div className="text-xs text-silver">Country</div>
        </div>
        <div className="text-xs text-foreground">{member.country}</div>
      </div>

      {/* Package Info */}
      {member.packageAmount > 0 && (
        <div className="mb-3 p-2 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-green-400" />
              <span className="text-sm font-medium text-green-400">{member.packageName}</span>
            </div>
            <div className="text-sm font-bold text-green-300">
              {formatCurrency(member.packageAmount)}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Row */}
      <div className="flex items-center justify-between text-xs text-silver">
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          <span>{formatDate(member.registrationDate)}</span>
        </div>
        
        <button
          onClick={() => toggleMemberDetails(member.id)}
          className="flex items-center gap-1 px-2 py-1 bg-primary/20 hover:bg-primary/30 rounded transition-colors text-primary"
        >
          <span>Details</span>
          <ChevronRight className={`h-3 w-3 transition-transform ${
            expandedMember === member.id ? 'rotate-90' : ''
          }`} />
        </button>
      </div>

      {/* Expanded Details */}
      {expandedMember === member.id && (
        <div className="mt-3 pt-3 border-t border-border bg-secondary/20 space-y-3">
          {/* Database Info */}
          <div className="flex items-center gap-2 text-xs text-silver">
            <Database className="h-3 w-3" />
            <span>Database: users/{member.dateFolder}/{member.dbKey}</span>
          </div>

          {/* Registration Location */}
          {member.registrationLocation && Object.keys(member.registrationLocation).length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-silver">
                <MapPin className="h-3 w-3" />
                <span>Registration Location</span>
              </div>
              <div className="text-xs text-foreground pl-5">
                {getLocationString(member.registrationLocation)}
              </div>
            </div>
          )}

          {/* Last Login */}
          {member.lastLogin && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-silver">
                <Activity className="h-3 w-3" />
                <span>Last Login</span>
              </div>
              <div className="text-xs text-foreground pl-5">
                {formatDate(member.lastLogin)} {formatTime(member.lastLogin)}
              </div>
              {member.lastLoginLocation && Object.keys(member.lastLoginLocation).length > 0 && (
                <div className="text-xs text-silver pl-5">
                  Location: {getLocationString(member.lastLoginLocation)}
                </div>
              )}
            </div>
          )}

          {/* Referral Link */}
          {member.referralLink && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-xs text-silver">
                <Link className="h-3 w-3" />
                <span>Referral Link</span>
              </div>
              <div className="text-xs text-foreground break-all pl-5">
                <a href={member.referralLink} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  {member.referralLink}
                </a>
              </div>
            </div>
          )}

          {/* Financial Info */}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/30">
            {member.walletBalance > 0 && (
              <div className="space-y-1">
                <div className="text-[10px] text-silver">Wallet</div>
                <div className="flex items-center gap-1 text-sm text-green-400">
                  <DollarSign className="h-3 w-3" />
                  <span className="font-medium">{formatCurrency(member.walletBalance)}</span>
                </div>
              </div>
            )}
            
            {member.fund > 0 && (
              <div className="space-y-1">
                <div className="text-[10px] text-silver">Fund</div>
                <div className="flex items-center gap-1 text-sm text-blue-400">
                  <DollarSign className="h-3 w-3" />
                  <span className="font-medium">{formatCurrency(member.fund)}</span>
                </div>
              </div>
            )}
            
            {member.referralCount > 0 && (
              <div className="space-y-1">
                <div className="text-[10px] text-silver">Referrals</div>
                <div className="flex items-center gap-1 text-sm text-purple-400">
                  <TrendingUp className="h-3 w-3" />
                  <span className="font-medium">{member.referralCount}</span>
                </div>
              </div>
            )}
          </div>

          {/* Role & Status */}
          <div className="flex gap-2 pt-2 border-t border-border/30">
            <div className={`px-2 py-1 rounded text-xs ${
              member.role === 'admin' 
                ? 'bg-red-500/10 text-red-400' 
                : 'bg-blue-500/10 text-blue-400'
            }`}>
              {member.role}
            </div>
            <div className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
              member.status === 'active' 
                ? 'bg-green-500/10 text-green-400' 
                : 'bg-red-500/10 text-red-400'
            }`}>
              {member.status === 'active' ? (
                <CheckCircle className="h-3 w-3" />
              ) : (
                <XCircle className="h-3 w-3" />
              )}
              {member.status}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Desktop Table Row Component - SPONSOR ID AS FIRST COLUMN
  const DesktopTableRow = ({ member, index }) => (
    <tr key={member.id} className="hover:bg-secondary/20 transition-colors">
      <td className="py-3 px-4 text-center">
        <div className="text-silver font-mono">#{index + 1}</div>
      </td>
      
      {/* SPONSOR ID COLUMN - FIRST AFTER # */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Shield className="h-4 w-4 text-purple-400" />
          <div>
            <div className="font-bold text-foreground text-sm">{member.sponsorId || 'N/A'}</div>
            <div className="text-xs text-silver">Sponsor ID</div>
          </div>
        </div>
      </td>
      
      {/* MEMBER INFO COLUMN */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-full border flex items-center justify-center ${
            member.status === 'active' 
              ? 'bg-green-500/10 border-green-500/20' 
              : 'bg-red-500/10 border-red-500/20'
          }`}>
            <User className={`h-4 w-4 ${
              member.status === 'active' ? 'text-green-400' : 'text-red-400'
            }`} />
          </div>
          <div>
            <div className="font-medium text-foreground text-sm">{member.fullName}</div>
            <div className="text-xs text-silver">{member.memberId}</div>
            <div className="text-[10px] text-silver mt-0.5">DB: {member.dateFolder}/{member.dbKey}</div>
          </div>
        </div>
      </td>
      
      {/* CONTACT COLUMN */}
      <td className="py-3 px-4">
        <div className="text-sm text-foreground truncate max-w-[180px]">{member.email}</div>
        <div className="text-xs text-silver mt-0.5">{member.mobile}</div>
      </td>
      
      {/* COUNTRY COLUMN */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Globe className="h-3 w-3 text-blue-400" />
          <span className="text-sm">{member.country}</span>
        </div>
      </td>
      
      {/* PACKAGE COLUMN */}
      <td className="py-3 px-4">
        <div className={`flex items-center gap-2 ${
          member.packageName === 'No Package' 
            ? 'text-red-400' 
            : 'text-green-400'
        }`}>
          <Package className="h-4 w-4" />
          <div className="text-sm font-medium">
            {member.packageName}
            {member.packageAmount > 0 && (
              <span className="text-xs ml-1 text-silver">
                ({formatCurrency(member.packageAmount)})
              </span>
            )}
          </div>
        </div>
      </td>
      
      {/* REGISTRATION DATE COLUMN */}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <Calendar className="h-3 w-3 text-silver" />
          <div>
            <div className="text-sm">{formatDate(member.registrationDate)}</div>
            <div className="text-xs text-silver">{formatTime(member.registrationDate)}</div>
          </div>
        </div>
        {member.lastLogin && (
          <div className="text-[10px] text-silver mt-1">
            Last: {formatTime(member.lastLogin)}
          </div>
        )}
      </td>
      
      {/* STATUS & ROLE COLUMN */}
      <td className="py-3 px-4">
        <div className="space-y-1">
          <div className={`px-3 py-1 rounded-full text-xs text-center ${
            member.status === 'active' 
              ? 'bg-green-500/20 text-green-400' 
              : 'bg-red-500/20 text-red-400'
          }`}>
            {member.status}
          </div>
          <div className={`px-3 py-1 rounded-full text-xs text-center ${
            member.role === 'admin' 
              ? 'bg-red-500/10 text-red-400' 
              : 'bg-blue-500/10 text-blue-400'
          }`}>
            {member.role}
          </div>
        </div>
      </td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-silver">Loading latest registrations...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-glow p-6 text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-foreground mb-2">Error Loading Data</h3>
        <p className="text-silver">{error}</p>
        <div className="mt-4 text-sm text-silver">
          <div className="flex items-center justify-center gap-2">
            <Database className="h-4 w-4" />
            <span>Database Path: users/date/userId</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-custom">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2 font-heading">Latest Registrations</h1>
            <p className="text-silver font-body">
              Showing {latestMembers.length} most recent members
            </p>
          </div>
          
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="card-glow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-silver font-body">Total Users</p>
              <p className="text-2xl font-bold text-foreground font-heading">{stats.totalUsers}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-500/10 flex items-center justify-center">
              <User className="h-6 w-6 text-blue-400" />
            </div>
          </div>
        </div>
        
        <div className="card-glow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-silver font-body">Active Users</p>
              <p className="text-2xl font-bold text-foreground font-heading">{stats.activeUsers}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
              <Activity className="h-6 w-6 text-green-400" />
            </div>
          </div>
        </div>
        
       
        
        <div className="card-glow p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-silver font-body">Today's Registrations</p>
              <p className="text-2xl font-bold text-foreground font-heading">{stats.todayRegistrations}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center">
              <TrendingUp className="h-6 w-6 text-orange-400" />
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Table View (hidden on mobile) */}
      <div className="hidden lg:block card-glow overflow-hidden mb-8">
        <div className="overflow-x-auto">
          <table className="w-full min-w-max">
            <thead className="bg-secondary/50">
              <tr>
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">#</th>
                {/* SPONSOR ID COLUMN - FIRST AFTER # */}
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">Sponsor ID</th>
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">Member Info</th>
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">Contact</th>
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">Country</th>
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">Package</th>
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">Registration Date</th>
                <th className="py-3 px-4 text-left text-silver font-heading font-semibold">Status & Role</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/30">
              {latestMembers.length > 0 ? (
                latestMembers.map((member, index) => (
                  <DesktopTableRow key={member.id} member={member} index={index} />
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="py-12 text-center">
                    <div className="text-primary text-6xl mb-4">👥</div>
                    <h3 className="text-xl font-semibold text-foreground font-heading mb-2">
                      No Members Found
                    </h3>
                    <p className="text-silver font-body">
                      No members have registered yet.
                    </p>
                    <div className="mt-4 text-sm text-silver">
                      <div className="flex items-center justify-center gap-2">
                        <Database className="h-4 w-4" />
                        <span>Checking: users/2026-02-06/*</span>
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Accordion View (visible on mobile) */}
      <div className="lg:hidden">
        {latestMembers.length > 0 ? (
          latestMembers.map((member, index) => (
            <MobileMemberCard key={member.id} member={member} index={index} />
          ))
        ) : (
          <div className="card-glow p-8 text-center">
            <div className="text-primary text-6xl mb-4">👥</div>
            <h3 className="text-xl font-semibold text-foreground font-heading mb-2">
              No Members Found
            </h3>
            <p className="text-silver font-body">
              No members have registered yet.
            </p>
            <div className="mt-4 text-sm text-silver">
              <div className="flex items-center justify-center gap-2">
                <Database className="h-4 w-4" />
                <span>Database: users/2026-02-06/*</span>
              </div>
            </div>
          </div>
        )}
      </div>

      
    </div>
  );
};

export default LatestRegistrations;